import {
  CheckCircleFilled,
  ClockCircleFilled,
  CloseCircleFilled,
  EditOutlined,
  InfoCircleFilled,
  MinusCircleFilled,
} from '@ant-design/icons';
import { Breadcrumb, Button } from 'antd';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';

/**
 * The desk's building blocks (doc 60), after AWS's console: every page says
 * where it is and what can be done, content sits in titled sections, and
 * details are read first, changed with «تعديل».
 */

/** Breadcrumb, title and status, a line about the record, the actions. */
export function PageHeader({
  breadcrumbs,
  title,
  status,
  description,
  actions,
}: {
  breadcrumbs?: { label: string; to?: string }[];
  title: ReactNode;
  status?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <header className="ui-page-header">
      {breadcrumbs && (
        <Breadcrumb
          className="ui-breadcrumb"
          items={breadcrumbs.map((b) => ({
            title: b.to ? <Link to={b.to}>{b.label}</Link> : b.label,
          }))}
        />
      )}
      <div className="ui-page-title-row">
        <h1 className="ui-page-title">
          {title}
          {status && <span className="ui-page-status">{status}</span>}
        </h1>
        {actions && <div className="ui-actions">{actions}</div>}
      </div>
      {description && <div className="ui-page-description">{description}</div>}
    </header>
  );
}

/** A titled white card: the unit of every page. */
export function Section({
  title,
  counter,
  description,
  actions,
  editing = false,
  children,
}: {
  title: ReactNode;
  /** «(3)» after the title. */
  counter?: number;
  description?: ReactNode;
  actions?: ReactNode;
  /** Marked while its form is open. */
  editing?: boolean;
  children: ReactNode;
}) {
  return (
    <section className={`ui-section${editing ? ' is-editing' : ''}`}>
      <div className="ui-section-head">
        <div>
          <h2 className="ui-section-title">
            {title}
            {counter !== undefined && (
              <span className="ui-counter ltr-num">({counter})</span>
            )}
          </h2>
          {description && (
            <div className="ui-section-description">{description}</div>
          )}
        </div>
        {actions && <div className="ui-actions">{actions}</div>}
      </div>
      <div className="ui-section-body">{children}</div>
    </section>
  );
}

export type KeyValue = {
  label: string;
  value: ReactNode;
  /** The whole row (long text). */
  wide?: boolean;
};

/** Read-only details: labels above values, in columns; empty reads «—». */
export function KeyValues({
  items,
  columns = 3,
}: {
  items: (KeyValue | false | null | undefined)[];
  columns?: 2 | 3 | 4;
}) {
  return (
    <dl className={`ui-key-values is-${columns}`}>
      {items.map(
        (item) =>
          item && (
            <div key={item.label} className={item.wide ? 'is-wide' : undefined}>
              <dt>{item.label}</dt>
              <dd>
                {item.value === null ||
                item.value === undefined ||
                item.value === '' ? (
                  <span className="ui-empty">—</span>
                ) : (
                  item.value
                )}
              </dd>
            </div>
          ),
      )}
    </dl>
  );
}

export type Tone = 'success' | 'warning' | 'error' | 'info' | 'stopped';

const TONE_ICONS: Record<Tone, ReactNode> = {
  success: <CheckCircleFilled />,
  warning: <ClockCircleFilled />,
  error: <CloseCircleFilled />,
  info: <InfoCircleFilled />,
  stopped: <MinusCircleFilled />,
};

/**
 * A status as an icon and a word, one colour per meaning everywhere:
 * done (green), waiting (orange), late or failed (red), information (blue),
 * stopped or inactive (grey).
 */
export function Status({
  tone,
  children,
}: {
  tone: Tone;
  children: ReactNode;
}) {
  return (
    <span className={`ui-status is-${tone}`}>
      {TONE_ICONS[tone]}
      <span>{children}</span>
    </span>
  );
}

/**
 * A section read first; «تعديل» opens its form in place. `edit` gets
 * `done` to call after saving or cancelling.
 */
export function EditableSection({
  title,
  description,
  canEdit,
  view,
  edit,
  extraActions,
}: {
  title: ReactNode;
  description?: ReactNode;
  canEdit: boolean;
  view: ReactNode;
  edit: (done: () => void) => ReactNode;
  extraActions?: ReactNode;
}) {
  const [editing, setEditing] = useState(false);
  return (
    <Section
      title={title}
      description={description}
      editing={editing}
      actions={
        editing ? undefined : (
          <>
            {extraActions}
            {canEdit && (
              <Button icon={<EditOutlined />} onClick={() => setEditing(true)}>
                تعديل
              </Button>
            )}
          </>
        )
      }
    >
      {editing ? edit(() => setEditing(false)) : view}
    </Section>
  );
}

/** A list page's header: title with its count, description, actions. */
export function ListHeader({
  title,
  count,
  description,
  actions,
}: {
  title: string;
  count?: number;
  description?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <PageHeader
      title={
        <>
          {title}
          {count !== undefined && (
            <span className="ui-counter ltr-num">({count})</span>
          )}
        </>
      }
      description={description}
      actions={actions}
    />
  );
}

/** The colour of each status, the same on every screen. */
export const MEMBER_TONE: Record<string, Tone> = {
  ACTIVE: 'success',
  LEFT: 'stopped',
};

export const METER_TONE: Record<string, Tone> = {
  CONNECTED: 'success',
  SUSPENDED: 'warning',
  DAMAGED: 'error',
  REPLACED: 'stopped',
  REMOVED: 'stopped',
  CLOSED: 'stopped',
};

export const PHONE_TONE: Record<string, Tone> = {
  OK: 'success',
  MISSING: 'stopped',
  INVALID: 'error',
  LANDLINE: 'warning',
};

export const BILL_TONE: Record<string, Tone> = {
  PAID: 'success',
  PENDING: 'warning',
  OVERDUE: 'error',
};

/** Keys typed in a field are the field's. */
const typing = (target: EventTarget | null) =>
  target instanceof HTMLElement &&
  !!target.closest('input, textarea, [contenteditable="true"], .ant-select');

/**
 * E opens a record's form (doc 61), unless the cursor is in a field or a
 * dialog is open. `enabled`: only while reading, and when editing is allowed.
 */
export function useEditKey(enabled: boolean, onEdit: () => void) {
  const editRef = useRef(onEdit);
  editRef.current = onEdit;
  useEffect(() => {
    if (!enabled) return;
    const onKey = (e: KeyboardEvent) => {
      if (
        e.code !== 'KeyE' ||
        e.ctrlKey ||
        e.metaKey ||
        e.altKey ||
        typing(e.target) ||
        document.querySelector('.ant-modal-wrap')
      ) {
        return;
      }
      e.preventDefault();
      editRef.current();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [enabled]);
}

/**
 * Under a record read in a drawer: its actions at the end (left in RTL),
 * «تعديل (E)» outermost, as in the page headers.
 */
export function ViewBar({
  onEdit,
  children,
}: {
  /** Absent: the record can't be edited (locked, or read-only role). */
  onEdit?: () => void;
  children?: ReactNode;
}) {
  return (
    <div className="ui-view-bar">
      {children}
      {onEdit && (
        <Button type="primary" icon={<EditOutlined />} onClick={onEdit}>
          تعديل (E)
        </Button>
      )}
    </div>
  );
}

/**
 * A list's table in a white card (doc 63): its filters on top, then the
 * table and its pages. Bars about the list (pending changes, selection) go
 * in `bars`, between the filters and the table.
 */
export function TableCard({
  toolbar,
  bars,
  children,
}: {
  toolbar?: ReactNode;
  bars?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="ui-table-card">
      {toolbar && <div className="ui-table-toolbar">{toolbar}</div>}
      {bars && <div className="ui-table-bars">{bars}</div>}
      {children}
    </div>
  );
}
